/**
 * A route is data: method, path, who may call it, and the schemas for params, query, body and response.
 * Route handlers (WP-API, WP-CONSOLE) and the mock handlers (below, for WP-PWA) are both driven by these
 * records, so the UI and the API can't disagree about a shape.
 */
import { z } from 'zod';
import type { ClientAction, StaffAction } from './roles.ts';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * public: no session (rate limited where marked). session: any signed-in user, no tenant. tenant: a member of
 * the tenant in the URL (`:t`), checked through `action`. staff: an Access-verified staff user, checked through
 * `staffAction`.
 */
export type RouteAuth = 'public' | 'session' | 'tenant' | 'staff';

/** What the body or response is on the wire. Everything is JSON unless stated. */
export type WireFormat = 'json' | 'binary' | 'csv' | 'pdf' | 'none';

export interface RouteDef<
  P extends z.ZodType = z.ZodType,
  Q extends z.ZodType = z.ZodType,
  B extends z.ZodType = z.ZodType,
  R extends z.ZodType = z.ZodType,
> {
  id: string;
  method: HttpMethod;
  /** Express-style path with `:param` segments, e.g. `/api/v1/t/:t/leads/:id`. */
  path: string;
  summary: string;
  auth: RouteAuth;
  action?: ClientAction;
  staffAction?: StaffAction;
  /** Step-up needed beyond what the action row says (e.g. for a single variant). */
  stepUp?: boolean;
  /** Named rate limit bucket (docs/53 §10). */
  rateLimit?: 'auth_start' | 'auth_verify' | 'api' | 'uploads' | 'exports' | 'forms' | 'help';
  params: P;
  query: Q;
  body: B;
  response: R;
  /** Success status. 200 unless stated. */
  status: number;
  requestFormat: WireFormat;
  responseFormat: WireFormat;
}

const NoParams = z.object({}).strict();
const NoQuery = z.object({}).strict();
const NoBody = z.undefined();

type Opt<T, D> = T extends undefined ? D : T;

export function defineRoute<
  P extends z.ZodType | undefined = undefined,
  Q extends z.ZodType | undefined = undefined,
  B extends z.ZodType | undefined = undefined,
  R extends z.ZodType = z.ZodType,
>(def: {
  id: string;
  method: HttpMethod;
  path: string;
  summary: string;
  auth: RouteAuth;
  action?: ClientAction;
  staffAction?: StaffAction;
  stepUp?: boolean;
  rateLimit?: RouteDef['rateLimit'];
  params?: P;
  query?: Q;
  body?: B;
  response: R;
  status?: number;
  requestFormat?: WireFormat;
  responseFormat?: WireFormat;
}): RouteDef<Opt<P, typeof NoParams>, Opt<Q, typeof NoQuery>, Opt<B, typeof NoBody>, R> {
  const out = {
    ...def,
    params: def.params ?? NoParams,
    query: def.query ?? NoQuery,
    body: def.body ?? NoBody,
    status: def.status ?? 200,
    requestFormat: def.requestFormat ?? (def.body ? 'json' : 'none'),
    responseFormat: def.responseFormat ?? 'json',
  };
  return out as unknown as RouteDef<Opt<P, typeof NoParams>, Opt<Q, typeof NoQuery>, Opt<B, typeof NoBody>, R>;
}

/** Path params are strings on the wire; these helpers keep the param objects strict. */
export function params<T extends z.ZodRawShape>(shape: T) {
  return z.object(shape).strict();
}

/** Compile a route path to a matcher. `/api/v1/t/:t/leads/:id` → regex with named groups. */
export function compilePath(path: string): { regex: RegExp; keys: string[] } {
  const keys: string[] = [];
  const pattern = path
    .split('/')
    .map((seg) => {
      if (seg.startsWith(':')) {
        keys.push(seg.slice(1));
        return '([^/]+)';
      }
      return seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return { regex: new RegExp(`^${pattern}$`), keys };
}

export function matchPath(path: string, pathname: string): Record<string, string> | null {
  const { regex, keys } = compilePath(path);
  const m = regex.exec(pathname);
  if (!m) return null;
  const out: Record<string, string> = {};
  keys.forEach((k, i) => {
    out[k] = decodeURIComponent(m[i + 1] ?? '');
  });
  return out;
}

/** Fill a route path with params, e.g. for links in notifications and mocks. */
export function buildPath(path: string, values: Record<string, string | number>): string {
  return path.replace(/:([A-Za-z_]+)/g, (_, k: string) => {
    const v = values[k];
    if (v === undefined) throw new Error(`missing path param ${k} for ${path}`);
    return encodeURIComponent(String(v));
  });
}

/** Requests that change state must carry this header (docs/53 §4.2 CSRF). */
export const CSRF_HEADER = 'X-FF-Req';
export const CSRF_HEADER_VALUE = '1';
